import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

interface UnsavedChangesContextValue {
  setDirty: (dirty: boolean) => void;
  /** Runs `action` immediately if nothing is dirty; otherwise shows a confirm dialog
   * and runs it only if the user chooses to discard changes. */
  guardNavigation: (action: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

// Scoped to DashboardLayout (mounted once per page, since each page renders its own
// <DashboardLayout>) rather than app-wide, so it only guards in-app navigation
// triggered by DashboardLayout's own UI (sidebar links, logo) while some child page
// has registered dirty state — e.g. QRCodeEditPage's Back button used to guard itself
// via a local dialog, but clicking a sidebar nav item bypassed it entirely and
// silently discarded changes. Native tab-close/refresh is still handled separately by
// each page's own beforeunload listener, since that can't go through React state.
export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  const dirtyRef = useRef(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const setDirty = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const guardNavigation = useCallback((action: () => void) => {
    if (!dirtyRef.current) {
      action();
      return;
    }
    pendingActionRef.current = action;
    setConfirmOpen(true);
  }, []);

  return (
    <UnsavedChangesContext.Provider value={{ setDirty, guardNavigation }}>
      {children}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white border border-[--clinqr-border] rounded-lg shadow-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold text-[--clinqr-text-primary]">
              Leave without saving?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[--clinqr-text-secondary]">
              You have unsaved changes. Leaving now will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-row justify-end gap-2">
            <AlertDialogCancel className="bg-white border border-[--clinqr-border] text-[--clinqr-text-primary] hover:bg-[--clinqr-bg-subtle]">
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const action = pendingActionRef.current;
                pendingActionRef.current = null;
                dirtyRef.current = false;
                setConfirmOpen(false);
                action?.();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Leave without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChangesGuard() {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error('useUnsavedChangesGuard must be used within a DashboardLayout');
  }
  return ctx;
}
